import { useState, useEffect, useRef } from 'react';
import { createCutoffTime } from 'utils/memoDateUtils';

/**
 * Hook to manage cutoff time for memo display
 * Resets cutoff when transitioning from expanded to collapsed
 * @param {boolean} memosExpanded - Whether memos are expanded
 * @param {Array} sortedMemos - The sorted memos array
 * @returns {string|null} The cutoff time ISO string
 */
export function useMemoCutoffTime(memosExpanded, sortedMemos) {
  const [cutoffTime, setCutoffTime] = useState(null);
  const prevExpanded = useRef(memosExpanded);

  useEffect(() => {
    const findLastNonPendingMemo = () => {
      return [...sortedMemos].reverse().find(m => !m.isPending);
    };

    const updateCutoffTime = () => {
      const lastNonPending = findLastNonPendingMemo();
      if (lastNonPending?.created_at) {
        const cutoff = createCutoffTime(lastNonPending.created_at);
        if (cutoff) {
          setCutoffTime(cutoff);
        }
      }
    };

    const hasMemoAfterCutoff = () => {
      if (!cutoffTime) return true; // If no cutoff, we're good
      // Check if there's at least one memo after the cutoff
      return sortedMemos.some(memo => memo.created_at && new Date(memo.created_at) > new Date(cutoffTime));
    };

    if ( sortedMemos.length === 0 ) {
      setCutoffTime(null);
      return;
    }

    // Reset cutoff time when transitioning from expanded -> unexpanded
    if (prevExpanded.current === true && memosExpanded === false) {
      updateCutoffTime();
    }
    // Initialize cutoff on first render if not expanded
    else if (!cutoffTime && !memosExpanded) {
      updateCutoffTime();
    }
    // Update cutoff when collapsed and there are no memos after cutoff (deletion happened)
    else if (!memosExpanded && cutoffTime && !hasMemoAfterCutoff()) {
      updateCutoffTime();
    }

    prevExpanded.current = memosExpanded;
  }, [memosExpanded, sortedMemos, cutoffTime]);

  return cutoffTime;
}